import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { LinearProgress } from '@mui/material'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import { CalendarComponent } from 'components/calendar'
import CardUserStudentComponent, { CardUserStudentActionProps } from 'components/card-user-student'
import { ActivitiesIcon, CommentIcon, MessagingDotIcon, ScheduleIcon, SeatIcon, UserBioIcon } from 'components/icons'
import TruncatedTextComponent from 'components/text-truncated'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import facebook from 'images/facebook.png'
import instagram from 'images/instagram.png'
import linkedin from 'images/linkedin.png'
import React, { useEffect, useState } from 'react'
import { NavigateFunction, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CalendarInputProps, CalendarType } from 'types/CalendarTypes'
import { MessageProps } from 'types/CommonTypes'
import { DefaultCarpoolType } from 'types/CommunityTypes'
import { RoomType } from 'types/RoomTypes'
import { UserPoolOutputProps } from 'types/UserTypes'
import {
  generateCalendarEvents,
  getCommunityArrivalTime,
  getCommunityDismissalTime,
  getDefaultedTime,
} from 'utils/calendar'
import { getCommunityCarpoolTypeLabel } from 'utils/communities'
import { isWeb, METERS_TO_MILES, parseJwt } from 'utils/constants'
import { createRoom } from 'utils/rooms'
import { getUserPool } from 'utils/users'
import './index.css'

function Index({
  tokens: { IdToken },
  failedAvatarUrls,
  onFailedAvatarUrl,
  onUpdate,
}: {
  tokens: any
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
  onUpdate: (cb?: () => void) => void
}): JSX.Element {
  // 1. State Hooks
  const params = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const userId: string = location.state?.userId ?? ''
  const comId: string = params?.comId ?? ''
  const userStudentId = searchParams.get('userStudentId') ?? ''
  const studentId = searchParams.get('studentId') ?? ''
  const poolType = searchParams.get('poolType') ?? ''
  const navigate: NavigateFunction = useNavigate()
  const [userPool, setUserPool] = useState<UserPoolOutputProps>()
  const [selectedCarpoolType, setSelectedCarpoolType] = useState<string>(poolType || DefaultCarpoolType.DISMISSAL)
  const [msg, setMsg] = useState<MessageProps>()
  const [isLoading, setIsLoading] = useState<string>('')

  // 2. Derived Constants
  const myUserId: string = parseJwt(IdToken).phone_number

  // 3. Effect Hooks
  useEffect(() => {
    (async function () {
      setIsLoading('Loading user...')
      const res = await getUserPool(userId, comId, userStudentId, studentId)
      if (res.error) setMsg(res.msg)
      else if (res.data) {
        setUserPool(res.data.userPool)
      }
      setIsLoading('')
    })()
  }, [userId, comId, userStudentId, studentId, navigate])

  // 4. Derived State, Validations and Early Returns
  if (!userPool) return <LinearProgress />
  const {
    // School Metadata
    schoolScheduleGroups,

    // User Details and Metadata
    userName,
    userBio,
    userPhotoLink,
    userSocialLinks,

    // Student Details and Metadata
    studentName,
    scheduleGroupName,
    studentActivities,
    userRelationship,
    userCompletedCarpools,
    userLastSeen,

    // Carpool Details
    pickupDistance,
    arrivalCarpoolRequestDefaultTime,
    arrivalCarpools = {},
    dropoffDistance,
    dismissalCarpoolRequestDefaultTime,
    dismissalCarpools = {},

    // Carpool Metadata
    pickupSeatsFilled,
    pickupSeatsAvailable,
    pickupComments,
    dropoffComments,

    // Messaging Details
    roomId,
  } = userPool

  // SOCIAL - check if social links are set
  // Prepare social media actions
  const socialActions: CardUserStudentActionProps[] = []
  const { facebook: facebookLink, instagram: instagramLink, linkedin: linkedinLink } = userSocialLinks ?? {}
  if (facebookLink) socialActions.push({ label: 'Facebook', icon: facebook, onClick: facebookLink })
  if (instagramLink) socialActions.push({ label: 'Instagram', icon: instagram, onClick: instagramLink })
  if (linkedinLink) socialActions.push({ label: 'LinkedIn', icon: linkedin, onClick: linkedinLink })

  // CARPOOL STATS - get "completed" carpools count and rides provided count by this user
  const drivesCompleted: string | undefined = userCompletedCarpools?.drivesCount
    ? `${userCompletedCarpools?.drivesCount} drives completed`
    : undefined
  const ridesProvided: string | undefined = userCompletedCarpools?.ridesCount
    ? `${userCompletedCarpools?.ridesCount} rides provided`
    : undefined

  // CARPOOL LOCATION DETAILS - only sent if both parties have shared their location
  const distance: number | undefined =
    poolType === DefaultCarpoolType.ARRIVAL
      ? pickupDistance
      : poolType === DefaultCarpoolType.DISMISSAL
        ? dropoffDistance
        : (dropoffDistance ?? pickupDistance)
  const distanceMiles: string | undefined = distance
    ? Math.round(distance * METERS_TO_MILES * 10) / 10 + ' miles'
    : undefined

  // CARPOOL SCHEDULE DETAILS - get carpool schedule by converting arrival/dismissal DateCarpoolProps to CalendarInputProps
  const schoolStartTime = getCommunityArrivalTime(schoolScheduleGroups, scheduleGroupName)
  const schoolEndTime = getCommunityDismissalTime(schoolScheduleGroups, scheduleGroupName)
  const defaultArrivalTime = getDefaultedTime(schoolStartTime, arrivalCarpoolRequestDefaultTime)
  const defaultDismissalTime = getDefaultedTime(schoolEndTime, dismissalCarpoolRequestDefaultTime)
  const arrivalEvents: CalendarInputProps[] = arrivalCarpools
    ? generateCalendarEvents(CalendarType.CARPOOL_REQUEST, {
        defaultTime: defaultArrivalTime,
        carpools: arrivalCarpools,
      })
    : []
  const dismissalEvents: CalendarInputProps[] = dismissalCarpools
    ? generateCalendarEvents(CalendarType.CARPOOL_REQUEST, {
        defaultTime: defaultDismissalTime,
        carpools: dismissalCarpools,
      })
    : []
  const events: CalendarInputProps[] =
    selectedCarpoolType === DefaultCarpoolType.ARRIVAL ? arrivalEvents : dismissalEvents

  // SEATS - check if carpool request enabled and if seats are filled or available
  const isPickupSeatsFilled: boolean = pickupSeatsFilled != null && pickupSeatsFilled > 0
  const isPickupSeatsAvailable: boolean = pickupSeatsAvailable != null && pickupSeatsAvailable > 0

  // COMMENTS - check if carpool request enabled and if comments are set
  const formattedPickupComments: JSX.Element[] | undefined = pickupComments
    ? pickupComments.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))
    : undefined
  const formattedDropoffComments: JSX.Element[] | undefined = dropoffComments
    ? dropoffComments.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))
    : undefined

  // 5. Event Handlers
  const handleMessage = async (roomId?: string) => {
    if (roomId) navigate(`/messaging/${roomId}`)
    else {
      // Create private chat room
      const { msg, data, error } = await createRoom({ roomType: RoomType.PRIVATE, roomUsers: [userId, myUserId] })
      if (error) setMsg(msg)
      else if (data) {
        onUpdate(() => navigate(`/messaging/${data.roomId}`))
      }
    }
  }

  // 6. Render
  const body: JSX.Element = (
    <div className={`flex flex-col flex-1 self-stretch gap-2 ${isWeb ? '' : 'px-5'}`}>
      {/* **************** Summary Profile & Pool Schedule **************** */}
      <CardUserStudentComponent
        userName={userName}
        photoLink={userPhotoLink}
        failedPhotoLinks={failedAvatarUrls}
        onFailedPhotoLink={onFailedAvatarUrl}
        userRelationship={userRelationship}
        distanceMiles={distanceMiles}
        studentName={studentName}
        scheduleGroupName={scheduleGroupName}
        drivesCompleted={drivesCompleted}
        ridesProvided={ridesProvided}
        actions={socialActions}
      />

      {/* Last Seen Information */}
      {userLastSeen && (
        <div className='flex items-center gap-2 text-sm text-gray-700 mt-1'>
          <AccessTimeIcon fontSize='small' className='text-gray-600' />
          <span>
            Last seen:{' '}
            {new Date(userLastSeen).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </span>
        </div>
      )}

      {/* **************** Activities **************** */}
      <div className='py-3'>
        <div className='flex items-center gap-2 mb-2'>
          <ActivitiesIcon />
          <div className='text-headings font-medium'>Activities</div>
        </div>

        {Array.isArray(studentActivities) && studentActivities.length > 0 ? (
          <div className={`flex flex-wrap items-center gap-1 `}>
            {studentActivities.map((activity: any, index: number) => (
              <div key={index} className='flex items-center gap-1'>
                <div className='text-sub-headings font-normal text-sm'>{activity}</div>
                {index < studentActivities.length - 1 && (
                  <div style={{ background: '#666', width: '5px', height: '5px', borderRadius: '50%' }}></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className='text-sm text-disable'>User has not configured any activities.</div>
        )}
      </div>

      {/* **************** Schedule **************** */}
      <div className='py-2'>
        <div className='flex justify-between items-center gap-3 mb-3'>
          <div className='flex items-center gap-2'>
            <ScheduleIcon />
            <h3 className='text-headings font-medium'>Schedule</h3>
          </div>
          <div className='flex gap-1'>
            {Object.values(DefaultCarpoolType).map((type) => {
              const label = getCommunityCarpoolTypeLabel(type)
              const isSelected = type === selectedCarpoolType
              return (
                <button
                  key={type}
                  onClick={() => setSelectedCarpoolType(isSelected ? '' : type)}
                  title={`${isSelected ? 'Deselect' : 'Select'} ${label}`}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    isSelected
                      ? 'bg-custom-yellow border-custom-yellow text-gray-900'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <div className='pt-2' data-testid='row-testid'>
          <CalendarComponent type={CalendarType.CARPOOL_REQUEST} events={events} isReadOnly={true} />
        </div>
      </div>

      {/* <div className='text-sub-headings text-sm py-2'>
        No carpool types are currently configured for this user.
      </div> */}

      {/* **************** User Bio **************** */}
      {userBio && (
        <div className='py-3'>
          <div className='flex items-center gap-2 mb-2'>
            <UserBioIcon />
            <div className='text-headings font-medium'>Get to Know This Family</div>
          </div>
          <div className='text-sm text-disable'>
            <TruncatedTextComponent text={userBio} />
          </div>
        </div>
      )}

      {/* **************** Carpooling Information **************** */}
      {(pickupComments || dropoffComments || isPickupSeatsFilled || isPickupSeatsAvailable) && (
        <div className='py-3'>
          <div className='flex items-center gap-2 mb-2'>
            <CommentIcon />
            <div className='text-headings font-medium'>Carpooling Information</div>
          </div>

          <div className='space-y-4'>
            {(isPickupSeatsFilled || isPickupSeatsAvailable) && (
              <div className='flex items-center gap-2 bg-gray-50 p-2 rounded'>
                <SeatIcon />
                <div className='text-sm text-disable'>
                  {isPickupSeatsFilled && `${pickupSeatsFilled} full`}
                  {isPickupSeatsFilled && isPickupSeatsAvailable ? ' • ' : ''}
                  {isPickupSeatsAvailable && `${pickupSeatsAvailable} available`}
                  {' • morning seats'}
                </div>
              </div>
            )}
            <div className='space-y-3'>
              {pickupComments && (
                <div className='space-y-1'>
                  <div className='text-sm font-medium text-headings'>Morning Notes</div>
                  <div className='text-sm text-disable'>{formattedPickupComments}</div>
                </div>
              )}
              {dropoffComments && (
                <div className='space-y-1'>
                  <div className='text-sm font-medium text-headings'>Afternoon Notes</div>
                  <div className='text-sm text-disable'>{formattedDropoffComments}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
  const footer: JSX.Element = (
    <ButtonMobileComponent
      text='Message now'
      color='bg-custom-yellow'
      width='w-full'
      onClick={() => handleMessage(roomId)}
      iconPosition='left'
      icon={<MessagingDotIcon />}
    />
  )

  return (
    <>
      {/***************** Display progress and error *****************/}
      {isLoading && <LinearProgress />}
      {msg && (
        <ToastComponent style={msg?.style} heading={msg?.heading} text={msg?.text} onClose={() => setMsg(undefined)} />
      )}
      {/***************** Mobile *****************/}
      {!isWeb && (
        <div className='flex flex-col min-h-screen'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text={userName} />
          </div>
          <div className='flex-1 pb-24'>{body}</div>
          <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>{footer}</div>
        </div>
      )}
      {/***************** Web *****************/}
      {isWeb && (
        <WizardComponent title={userName}>
          <>
            {body}
            {footer}
          </>
        </WizardComponent>
      )}
    </>
  )
}

export default Index
